import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";

// Observed end-to-end analysis time is 55-80 s; cache hits finish sooner.
const EST_MS = 75000;
const PHASES = [
  { id: "queued", label: "analysis.phaseQueued", floor: 2, cap: 8 },
  { id: "upload", label: "analysis.phaseUpload", floor: 8, cap: 18 },
  { id: "ai_vision", label: "analysis.phaseAiVision", floor: 18, cap: 85 },
  { id: "calculation", label: "analysis.phaseCalculation", floor: 88, cap: 97 },
];

export function useElapsed(startedAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return Math.max(0, now - (startedAt || now));
}

export function analysisProgress(stage, elapsed) {
  const idx = Math.max(0, PHASES.findIndex((p) => p.id === stage));
  const phase = PHASES[idx];
  const pct = Math.min(phase.cap, Math.max(phase.floor, (elapsed / EST_MS) * 100));
  return { idx, label: phase.label, pct: Math.round(pct), remainingS: Math.ceil(Math.max(0, EST_MS - elapsed) / 1000) };
}

function PhaseList({ idx, t }) {
  return (
    <ol className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
      {PHASES.map((p, i) => (
        <li key={p.id} data-testid={`analysis-phase-${p.id}`}
          className={`flex items-center gap-1.5 text-xs ${i < idx ? "text-emerald-400" : i === idx ? "text-blue-300" : "text-slate-500"}`}>
          {i < idx ? <Check size={12}/> : i === idx ? <Loader2 size={12} className="animate-spin"/> : <span className="w-3 h-3 rounded-full border border-slate-600"/>}
          {t(p.label)}
        </li>
      ))}
    </ol>
  );
}

export default function AnalysisProgress({ stage, startedAt }) {
  const { t } = useTranslation();
  const elapsed = useElapsed(startedAt);
  const { idx, label, pct, remainingS } = analysisProgress(stage, elapsed);
  return (
    <div className="max-w-xl mx-auto text-left" data-testid="analysis-progress">
      <div className="flex items-center justify-between gap-3 text-sm mb-2">
        <span className="text-slate-100 font-medium" data-testid="analysis-stage">{t(label)}</span>
        <span className="font-mono-data text-slate-400" data-testid="analysis-eta">
          {remainingS > 0 ? t("analysis.remaining", { s: remainingS }) : t("analysis.almostDone")}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full bg-blue-500 transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} data-testid="analysis-progress-bar"/>
      </div>
      <PhaseList idx={idx} t={t}/>
      <p className="mt-3 text-xs text-slate-500">{t("analysis.estimate")}</p>
      <div className="mt-4 px-4 py-3 rounded-md bg-blue-500/10 border border-blue-500/30 text-sm text-blue-200" data-testid="analysis-can-leave">
        {t("analysis.canLeave")}
      </div>
    </div>
  );
}
